import { isPresent } from '@luminovo/commons';
import { Checkbox, Radio } from '@luminovo/design-system';
import React, { createContext, type JSX, useCallback, useContext, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { DisplayColumnDef, Row, RowData, Table } from '../tanstackTypes';

export const DEFAULT_SELECT_COLUMN_ID = 'select-col';

interface SelectionContextValue {
    anchorRef: React.MutableRefObject<string | undefined>;
    hoveredRowRef: React.MutableRefObject<{ id: string; toggle: () => void } | undefined>;
}

const SelectionContext = createContext<SelectionContextValue | undefined>(undefined);

/** Holds the shift-click anchor and the hovered row that the `x` key toggles. */
export function SelectionAnchorProvider({ children }: { children: React.ReactNode }): JSX.Element {
    const anchorRef = useRef<string | undefined>(undefined);
    const hoveredRowRef = useRef<{ id: string; toggle: () => void } | undefined>(undefined);

    const value = React.useMemo(() => ({ anchorRef, hoveredRowRef }), []);

    return <SelectionContext.Provider value={value}>{children}</SelectionContext.Provider>;
}

function useSelectionContext(): SelectionContextValue | undefined {
    return useContext(SelectionContext);
}

/** Toggles the hovered row's selection on `x`. */
export function useXKeySelectionHotkey(): void {
    const context = useSelectionContext();

    const callback = useCallback(() => {
        context?.hoveredRowRef.current?.toggle();
        // oxlint-disable-next-line react/preserve-manual-memoization -- SOU-4700: the dependency list is narrower than what the compiler infers (typically an optional chain); widening it would recompute the memo more often and hand consumers a new identity
    }, [context]);

    useHotkeys('x', callback, {
        enableOnFormTags: false,
        enableOnContentEditable: false,
    });
}

/** Makes the row the `x` key's target while hovered. Handlers aren't memoized: TanStack rows aren't stable. */
export function useRowHoverSelection<TData extends RowData>(
    row: Row<TData>,
): {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
} {
    const context = useSelectionContext();
    const rowId = row.id;

    // The ref writes below happen in mouse handlers, well after render, which the rule cannot see.
    return {
        onMouseEnter: () => {
            if (context && row.getCanSelect()) {
                // oxlint-disable-next-line react/immutability
                context.hoveredRowRef.current = {
                    id: rowId,
                    toggle: () => row.toggleSelected(),
                };
            }
        },
        onMouseLeave: () => {
            if (context && context.hoveredRowRef.current?.id === rowId) {
                // oxlint-disable-next-line react/immutability
                context.hoveredRowRef.current = undefined;
            }
        },
    };
}

/** As in Linear: a click toggles the row and sets the anchor, or clears it; shift+click adds the range from it. */
function handleSelectionClick<TData extends RowData>(
    event: React.MouseEvent,
    row: Row<TData>,
    table: Table<TData>,
    context: SelectionContextValue | undefined,
): void {
    const anchorRef = context?.anchorRef;
    const visibleRows = table.getRowModel().rows;
    const currentIndex = visibleRows.findIndex((r) => r.id === row.id);

    const anchorRow = isPresent(anchorRef?.current) ? visibleRows.find((r) => r.id === anchorRef.current) : undefined;

    if (event.shiftKey && isPresent(anchorRow)) {
        const anchorIndex = visibleRows.indexOf(anchorRow);
        const [start, end] = [Math.min(anchorIndex, currentIndex), Math.max(anchorIndex, currentIndex)];

        const rangeSelection = Object.fromEntries(
            visibleRows
                .slice(start, end + 1)
                .filter((r) => r.getCanSelect())
                .map((r) => [r.id, true] as const),
        );

        table.setRowSelection((prev) => ({ ...prev, ...rangeSelection }));
    } else {
        // Normal click OR anchor was filtered out
        const wasSelected = row.getIsSelected();

        if (wasSelected) {
            table.setRowSelection((prev) => {
                const next = { ...prev };
                delete next[row.id];
                return next;
            });
            if (anchorRef) {
                anchorRef.current = undefined;
            }
        } else {
            table.setRowSelection((prev) => ({ ...prev, [row.id]: true }));
            if (anchorRef) {
                anchorRef.current = row.id;
            }
        }
    }
}

export function SelectionCheckbox<TData extends RowData>({
    row,
    table,
}: {
    row: Row<TData>;
    table: Table<TData>;
}): JSX.Element {
    const context = useSelectionContext();

    return (
        <Checkbox
            size="small"
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onClick={(event) => {
                event.stopPropagation();
                handleSelectionClick(event, row, table, context);
            }}
            // A no-op: `onClick` does the selecting.
            onChange={() => {}}
        />
    );
}

/**
 * Checkbox for parent rows in sub-row selection tables.
 * Derives checked state from sub-rows so the parent reflects the union of its children
 * even when its own ID isn't in `rowSelection`.
 */
function ParentSelectionCheckbox<TData extends RowData>({ row }: { row: Row<TData> }): JSX.Element {
    const checked = row.getIsAllSubRowsSelected();
    const indeterminate = row.getIsSomeSelected();

    return (
        <Checkbox
            size="small"
            checked={checked}
            indeterminate={indeterminate}
            disabled={!row.getCanSelect()}
            onClick={(event) => {
                event.stopPropagation();
                row.toggleSelected(!checked);
            }}
            onChange={() => {}}
        />
    );
}

/** Multi-row selection with checkboxes; shift+click selects a range. */
export const DefaultColumnSelection: DisplayColumnDef<any, unknown> = {
    id: DEFAULT_SELECT_COLUMN_ID,
    size: 40,
    maxSize: 40,
    header: ({ table }) => (
        <Checkbox
            size="small"
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
        />
    ),
    cell: ({ row, table }) => {
        if (row.depth > 0) {
            return <></>;
        }

        return <SelectionCheckbox row={row} table={table} />;
    },
    enableHiding: false,
    meta: {
        initialPinning: 'start',
        dataType: 'generic',
        enableOnRowClick: false,
        label: () => '',
    },
};

/**
 * Default column definition for multi-row selection that also supports sub-rows.
 * Parents derive checked state from their children; sub-rows use the standard
 * SelectionCheckbox (with shift-click + 'x'-key hover toggle).
 */
export const DefaultColumnSelectionWithSubRows: DisplayColumnDef<any, unknown> = {
    id: DEFAULT_SELECT_COLUMN_ID,
    size: 40,
    maxSize: 40,
    header: ({ table }) => (
        <Checkbox
            size="small"
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
        />
    ),
    cell: ({ row, table }) => {
        if (row.subRows.length > 0) {
            return <ParentSelectionCheckbox row={row} />;
        }
        return <SelectionCheckbox row={row} table={table} />;
    },
    enableHiding: false,
    meta: {
        initialPinning: 'start',
        dataType: 'generic',
        enableOnRowClick: false,
        label: () => '',
    },
};

/** Single-row selection with radio buttons. */
export const DefaultColumnSingleSelection: DisplayColumnDef<any, unknown> = {
    id: DEFAULT_SELECT_COLUMN_ID,
    size: 40,
    maxSize: 40,
    header: () => <></>,
    cell: ({ row, table }) => (
        <Radio
            size="small"
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onChange={() => {
                table.resetRowSelection(true);
                row.toggleSelected();
            }}
        />
    ),
    enableHiding: false,
    meta: {
        initialPinning: 'start',
        dataType: 'generic',
        enableOnRowClick: false,
        label: () => '',
    },
};
